import React, { useEffect, useState, useMemo } from "react";
import { api, fmtMoney, todayISO } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, TrendingDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const CURRENT_YEAR = new Date().getFullYear();

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [open, setOpen] = useState(false);

  const load = () =>
    api.get(`/expenses?year=${year}`).then((r) => setRows(r.data || []));

  useEffect(() => {
    api.get("/expenses/categories").then((r) => setCategories(r.data.categories || []));
  }, []);

  useEffect(() => { load(); }, [year]);

  const summary = useMemo(() => {
    const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
    const byCategory = {};
    rows.forEach((r) => {
      byCategory[r.category] = (byCategory[r.category] || 0) + Number(r.amount || 0);
    });
    return { total, byCategory, count: rows.length };
  }, [rows]);

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Expense deleted");
    load();
  };

  return (
    <div className="space-y-8" data-testid="expenses-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Command Center · Deductibles</div>
          <h1 className="font-heading text-5xl mt-2">Expense Tracker</h1>
          <p className="text-sm text-[#050303]/60 mt-2">
            Log every marketing dollar, MLS fee, and mile so your year-end P&L is tax-ready.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32" data-testid="expense-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <NewExpenseDialog open={open} setOpen={setOpen} categories={categories} onCreate={load} />
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="expense-summary">
        <SummaryCard
          icon={<TrendingDown size={18} strokeWidth={1.5} />}
          label="Total Deducted"
          value={fmtMoney(summary.total)}
          sub={`${summary.count} entr${summary.count === 1 ? "y" : "ies"} · ${year}`}
        />
        <SummaryCard
          icon={<Receipt size={18} strokeWidth={1.5} />}
          label="Top Category"
          value={topCategoryName(summary.byCategory)}
          sub={fmtMoney(topCategoryAmt(summary.byCategory))}
        />
        <SummaryCard
          icon={<Receipt size={18} strokeWidth={1.5} />}
          label="Avg. per Entry"
          value={fmtMoney(summary.count ? summary.total / summary.count : 0)}
          sub="Rolling YTD"
        />
      </section>

      <section className="core-card p-6" data-testid="expenses-table-card">
        <div className="overline mb-4">All expenses · {year}</div>
        {rows.length === 0 ? (
          <div className="text-sm text-[#050303]/50 py-10 text-center">
            No expenses logged for {year}. Click &quot;New Expense&quot; above to add your first.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-[#d1cece]">
              <tr className="text-left text-[#050303]/70">
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4">Category</th>
                <th className="py-2 pr-4">Vendor</th>
                <th className="py-2 pr-4 text-right">Amount</th>
                <th className="py-2 pr-4">Notes</th>
                <th className="py-2 w-10"></th>
              </tr>
            </thead>
            <tbody data-testid="expense-rows">
              {rows.map((e) => (
                <tr key={e.id} className="border-b border-[#d1cece]/50" data-testid={`expense-row-${e.id}`}>
                  <td className="py-2.5 pr-4">{e.date}</td>
                  <td className="py-2.5 pr-4">
                    <span className="px-2 py-0.5 rounded-full bg-[#e1b7c1]/40 text-xs">{e.category}</span>
                  </td>
                  <td className="py-2.5 pr-4">{e.vendor || "—"}</td>
                  <td className="py-2.5 pr-4 text-right font-semibold">{fmtMoney(e.amount)}</td>
                  <td className="py-2.5 pr-4 text-[#050303]/60 max-w-[240px] truncate">{e.notes || "—"}</td>
                  <td>
                    <button
                      onClick={() => del(e.id)}
                      className="text-[#050303]/40 hover:text-[#bf858e]"
                      data-testid={`delete-expense-${e.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function SummaryCard({ icon, label, value, sub }) {
  return (
    <div className="core-card p-6" data-testid={`expense-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        <div className="text-[#bf858e]">{icon}</div>
      </div>
      <div className="font-heading text-3xl tracking-tight">{value}</div>
      {sub && <div className="text-xs text-[#050303]/55 mt-2">{sub}</div>}
    </div>
  );
}

function topCategoryName(byCat) {
  const entries = Object.entries(byCat);
  if (entries.length === 0) return "—";
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}
function topCategoryAmt(byCat) {
  const entries = Object.entries(byCat);
  if (entries.length === 0) return 0;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][1];
}

function NewExpenseDialog({ open, setOpen, categories, onCreate }) {
  const [form, setForm] = useState({
    date: todayISO(),
    category: categories[0] || "Marketing",
    amount: "",
    vendor: "",
    notes: "",
  });

  useEffect(() => {
    if (categories.length && !form.category) {
      setForm((f) => ({ ...f, category: categories[0] }));
    }
  }, [categories]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Expense logged");
      setOpen(false);
      setForm({
        date: todayISO(),
        category: categories[0] || "Marketing",
        amount: "",
        vendor: "",
        notes: "",
      });
      onCreate();
    } catch {
      toast.error("Failed to save");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex items-center gap-2 text-sm"
          data-testid="new-expense-btn"
        >
          <Plus size={16} /> New Expense
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Log an Expense</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="new-expense-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input
                data-testid="expense-date"
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input
                data-testid="expense-amount"
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Category</Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}
            >
              <SelectTrigger data-testid="expense-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Vendor / Payee</Label>
            <Input
              data-testid="expense-vendor"
              placeholder="e.g. Canva, USPS, Georgia MLS…"
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            />
          </div>
          <div>
            <Label>Notes</Label>
            <Input
              data-testid="expense-notes"
              placeholder="Optional context"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <button
            type="submit"
            className="w-full bg-[#050303] text-white pill-btn hover:bg-[#bf858e]"
            data-testid="submit-new-expense"
          >
            Save Expense
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
